import { getTwoDigitFormattedPrice } from '@atlassiansox/bxp-use-pricing';
import { createHook } from 'react-sweet-state';
import {
  PRODUCT_FAMILY_NAME_BY_PRODUCT_NAME,
  PRODUCT_NAMES_BY_PRODUCT_FAMILY_NAME
} from './constants';
import {
  GlobalState,
  ProductFamilyName,
  ProductName,
  marketplaceAddonDetails
} from './models';
import { store } from './store';

const getProductFamilyNames = ({ selectedProductNames }: GlobalState) => {
  const productFamilyNames = new Set<ProductFamilyName>();
  for (const productName of Array.from(selectedProductNames.values())) {
    productFamilyNames.add(PRODUCT_FAMILY_NAME_BY_PRODUCT_NAME[productName]);
  }

  return Array.from(productFamilyNames.values());
};

export const useSelectedProductFamilyNames = createHook(store, {
  selector: getProductFamilyNames
});

// if productFamilyName is supplied then return all selected product names
// in that product family, otherwise just return all selected product names
const getProductNames = (
  { selectedProductNames }: GlobalState,
  productFamilyName?: ProductFamilyName
) => {
  if (!productFamilyName) {
    return Array.from(selectedProductNames.values());
  }

  const productNames: ProductName[] = [];
  selectedProductNames.forEach(
    (productName) =>
      PRODUCT_NAMES_BY_PRODUCT_FAMILY_NAME[productFamilyName]?.includes(
        productName
      ) && productNames.push(productName)
  );

  return productNames;
};

export const useSelectedProductNames = createHook(store, {
  selector: getProductNames
});

export const getMarketPlaceApps = (
  { selectedMarketplaceAddons }: GlobalState,
  productFamilyName?: ProductFamilyName
): Set<marketplaceAddonDetails> => {
  if (!productFamilyName) {
    return new Set();
  }

  return selectedMarketplaceAddons.get(productFamilyName) ?? new Set();
};

export const useSelectedMarketplaceApps = createHook(store, {
  selector: getMarketPlaceApps
});

export const getHighestUnitCount = (
  state: GlobalState,
  productFamilyName: ProductFamilyName
): number => {
  const { unitCountByProduct } = state;

  const productNames = getProductNames(state, productFamilyName);
  let highestUnitCount = 0;

  for (const productName of productNames) {
    highestUnitCount = Math.max(
      unitCountByProduct.get(productName) || 0,
      highestUnitCount
    );
  }
  return highestUnitCount;
};

export const useHighestUnitCount = createHook(store, {
  selector: getHighestUnitCount
});

const getFormattedProductFamilyTotalPrice = (
  { priceTotalByProductFamily }: GlobalState,
  productFamilyName: ProductFamilyName
) => {
  const totalPrice = priceTotalByProductFamily[productFamilyName];
  const formattedTotalPrice = getTwoDigitFormattedPrice('USD', totalPrice || 0);

  return { totalPrice, formattedTotalPrice };
};

export const useProductFamilyTotal = createHook(store, {
  selector: getFormattedProductFamilyTotalPrice
});

const getFormattedTotalPrice = ({ priceTotalByProductFamily }: GlobalState) => {
  const totalPrice = Object.values(priceTotalByProductFamily).reduce(
    (total, productFamilyTotal) => total + productFamilyTotal,
    0
  );

  return getTwoDigitFormattedPrice('USD', totalPrice || 0);
};

export const useTotal = createHook(store, {
  selector: getFormattedTotalPrice
});

const getIsProductFamilyFree = (
  state: GlobalState,
  productFamilyName: ProductFamilyName
) => {
  const productsInFamily = getProductNames(state, productFamilyName);
  return productsInFamily.every((productName: ProductName) =>
    state.freePlanProducts.has(productName)
  );
};

export const useIsProductFamilyFree = createHook(store, {
  selector: getIsProductFamilyFree
});

const getAppErrors = (state: GlobalState) => state.appErrors;
export const useAppErrors = createHook(store, {
  selector: getAppErrors
});
